import React, { useCallback, useEffect, useState } from 'react';
import type { FC, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { BrandPanel } from '../../components/BrandPanel/BrandPanel';
import { QrModal } from '../../components/QrModal/QrModal';
import { Button } from '../../components/ui/Button/Button';
import { useToast } from '../../components/ui/Toast/ToastProvider';
import { useResetPasswordMutation } from '../../entities/session/api/sessionApi';
import { cx } from '../../utils/cx';
import styles from './ResetPasswordPage.module.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zа-я]{2,}$/i;

const RESEND_SECONDS = 120;

export const ResetPasswordPage: FC = () => {
    const notify = useToast();

    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState(false);
    const [timeLeft, setTimeLeft] = useState(RESEND_SECONDS);
    const [qrOpen, setQrOpen] = useState(false);

    const [resetPassword, { isLoading }] = useResetPasswordMutation();

    useEffect(() => {
        if (!sent || timeLeft <= 0) return undefined;

        const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [sent, timeLeft]);

    const send = useCallback(async (): Promise<void> => {
        const value = email.trim();

        if (!EMAIL_RE.test(value)) {
            setError('Укажите почту в формате name@company.com');
            return;
        }

        setError(null);

        try {
            await resetPassword({ email: value, url: window.location.origin }).unwrap();

            setSent(true);
            setTimeLeft(RESEND_SECONDS);
        } catch {
            /* тост об ошибке уже показал notifyOnError */
        }
    }, [email, resetPassword]);

    const resend = useCallback(async (): Promise<void> => {
        await send();
        notify('Письмо отправлено повторно');
    }, [send, notify]);

    const frame = (title: string, lead: ReactNode, content: ReactNode): ReactElement => (
        <>
            <main className={styles.page}>
                <BrandPanel onOpenQr={() => setQrOpen(true)} />

                <section className={styles.panel}>
                    <div className={styles.main}>
                        <h2 className={styles.heading}>{title}</h2>
                        <p className={styles.description}>{lead}</p>

                        <div className={styles.body}>{content}</div>

                        <div className={styles.footer}>
                            <Link className={styles.back} to="/login">
                                ← Вернуться ко входу
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
        </>
    );

    if (sent) {
        const minutes = Math.floor(timeLeft / 60);
        const seconds = String(timeLeft % 60).padStart(2, '0');

        return frame(
            'Проверьте почту',
            <>
                Отправили письмо на <b>{email.trim()}</b>. Перейдите по ссылке из него,
                чтобы задать новый пароль.
            </>,
            <>
                <p className={styles.hint}>
                    {timeLeft > 0
                        ? `Письма нет? Проверьте папку «Спам» — отправить повторно можно через ${minutes}:${seconds}`
                        : 'Письма всё ещё нет? Проверьте папку «Спам» или отправьте его заново.'}
                </p>

                <Button
                    variant="primary"
                    disabled={timeLeft > 0 || isLoading}
                    onClick={resend}
                >
                    {isLoading ? 'Отправляем...' : 'Отправить повторно'}
                </Button>
            </>,
        );
    }

    return frame(
        'Восстановление пароля',
        'Укажите почту, на которую зарегистрирован аккаунт — пришлём ссылку для смены пароля.',
        <>
            <div className={styles.field}>
                <label className={styles.label} htmlFor="email">
                    Электронная почта
                </label>

                <input
                    id="email"
                    type="email"
                    className={cx(styles.input, error && styles.inputError)}
                    value={email}
                    placeholder="name@company.com"
                    autoComplete="email"
                    autoFocus
                    aria-invalid={Boolean(error)}
                    onChange={(event) => {
                        setEmail(event.target.value);
                        if (EMAIL_RE.test(event.target.value.trim())) setError(null);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') void send();
                    }}
                />

                {error && <span className={styles.error}>{error}</span>}
            </div>

            <Button variant="primary" disabled={isLoading} onClick={send}>
                {isLoading ? 'Отправляем...' : 'Отправить ссылку'}
            </Button>
        </>,
    );
};